import React, { useMemo, useCallback, useRef, useEffect, useImperativeHandle, forwardRef, useState } from 'react';
import Plot from 'react-plotly.js';
import Plotly from 'plotly.js';
import { MetricRow, SelectedMetric, ChartSettings, ChartAnnotation, ThresholdLine, BatchOverlay } from '../types';

interface PlotlyChartProps {
  data: MetricRow[];
  dataExtent?: { from: Date; to: Date } | null; // Stable time extent for rangeslider (doesn't change on zoom)
  selectedMetrics: SelectedMetric[];
  chartSettings: ChartSettings;
  annotations?: ChartAnnotation[];
  thresholds?: ThresholdLine[];
  batchOverlays?: BatchOverlay[]; // Periods already assigned to a batch
  selectionMode?: boolean; // When true, dragging selects an x-range instead of zooming
  onRangeSelect?: (from: Date, to: Date) => void; // Fired when a range is selected in selectionMode
  onZoomChange?: (from: Date | null, to: Date | null) => void;
  onAddAnnotation?: (timestamp: string, value: number) => void;
  height?: number;
  resizeTrigger?: number; // Increment this to force a resize
  resetRangeTrigger?: number; // Increment this to reset the chart range to full data extent
}

export interface PlotlyChartHandle {
  resize: () => void;
}

/**
 * Plotly renders date axes in UTC and returns relayout/selection boundaries as
 * strings like "2026-06-23 09:00:00.000" (UTC, with no timezone designator).
 * Parsing those with `new Date(str)` would interpret them as *local* time and
 * shift the value by the browser's timezone offset (e.g. -2h at UTC+2). We
 * normalize them to UTC explicitly so a selected range matches the plotted data.
 */
function parsePlotlyAxisDate(v: any): Date {
  if (v instanceof Date) return v;
  if (typeof v === 'number') return new Date(v);
  const s = String(v).trim().replace(' ', 'T');
  const hasTz = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(s);
  return new Date(hasTz ? s : s + 'Z');
}

// Dark theme colors
const PLOTLY_LAYOUT_DEFAULTS: Partial<Plotly.Layout> = {
  paper_bgcolor: 'rgba(33, 40, 44, 0.9)',
  plot_bgcolor: 'rgba(33, 40, 44, 0.6)',
  font: {
    color: '#c5c5c5',
    family: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    size: 12,
  },
  margin: { l: 60, r: 30, t: 30, b: 50 },
  hovermode: 'x unified',
  hoverlabel: {
    bgcolor: 'rgba(33, 40, 44, 0.95)',
    bordercolor: 'rgba(108, 117, 125, 0.3)',
    font: { color: '#c5c5c5', size: 12 },
  },
  legend: {
    orientation: 'h',
    yanchor: 'bottom',
    y: 1.02,
    xanchor: 'right',
    x: 1,
    bgcolor: 'transparent',
    font: { size: 11 },
  },
  xaxis: {
    type: 'date',
    gridcolor: 'rgba(108, 117, 125, 0.15)',
    zerolinecolor: 'rgba(108, 117, 125, 0.2)',
    tickfont: { color: '#8b949e', size: 11 },
    title: { text: '', font: { color: '#8b949e', size: 12 } },
  },
  yaxis: {
    gridcolor: 'rgba(108, 117, 125, 0.15)',
    zerolinecolor: 'rgba(108, 117, 125, 0.2)',
    tickfont: { color: '#8b949e', size: 11 },
    title: { text: 'Value', font: { color: '#8b949e', size: 12 } },
  },
  modebar: {
    bgcolor: 'transparent',
    color: '#8b949e',
    activecolor: '#53bde4',
  },
};

const PLOTLY_CONFIG: Partial<Plotly.Config> = {
  displayModeBar: true,
  displaylogo: false,
  modeBarButtonsToRemove: ['lasso2d', 'select2d', 'autoScale2d'] as any,
  scrollZoom: true,
  responsive: true,
};

export const PlotlyChart = forwardRef<PlotlyChartHandle, PlotlyChartProps>(function PlotlyChart({
  data,
  dataExtent,
  selectedMetrics,
  chartSettings,
  annotations = [],
  thresholds = [],
  batchOverlays = [],
  selectionMode = false,
  onRangeSelect,
  onZoomChange,
  onAddAnnotation,
  height = 500,
  resizeTrigger,
  resetRangeTrigger,
}, ref) {
  const plotRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const lastResetTriggerRef = useRef(resetRangeTrigger);
  // Track the current zoom range - use state so Y-axis recalculates when range changes
  const [currentRange, setCurrentRange] = useState<{ from: string | null; to: string | null }>({ from: null, to: null });

  // Expose resize method via ref
  useImperativeHandle(ref, () => ({
    resize: () => {
      if (containerRef.current) {
        const plotDiv = containerRef.current.querySelector('.js-plotly-plot') as HTMLElement;
        if (plotDiv) {
          Plotly.Plots.resize(plotDiv);
        }
      }
    },
  }));

  // Trigger resize when resizeTrigger changes
  useEffect(() => {
    if (resizeTrigger !== undefined && containerRef.current) {
      // Small delay to allow DOM to update
      const timer = setTimeout(() => {
        const plotDiv = containerRef.current?.querySelector('.js-plotly-plot') as HTMLElement;
        if (plotDiv) {
          Plotly.Plots.resize(plotDiv);
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [resizeTrigger]);

  // Transform data into Plotly traces
  const traces = useMemo(() => {
    const visibleMetrics = selectedMetrics.filter(m => m.visible);

    if (data.length === 0 || visibleMetrics.length === 0) {
      return [];
    }

    // Sort data by timestamp
    const sortedData = [...data].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );

    // Build timestamps array - convert to Date objects for local timezone display
    const timestamps = sortedData.map(row => new Date(row.createdAt));

    // Create traces for each visible metric
    const plotTraces: Plotly.Data[] = visibleMetrics.map((metric, index) => {
      const values = sortedData.map(row => {
        const val = row.metrics?.[metric.key];
        return typeof val === 'number' && Number.isFinite(val) ? val : null;
      });

      // Determine line style based on settings and index
      const dashPattern = index % 3 === 1 ? 'dot' : index % 3 === 2 ? 'dash' : 'solid';

      const trace: Plotly.Data = {
        type: 'scatter',
        mode: chartSettings.showPoints ? 'lines+markers' : 'lines',
        name: metric.label,
        x: timestamps,
        y: values,
        line: {
          color: metric.color,
          width: chartSettings.lineWidth,
          dash: visibleMetrics.length > 8 ? dashPattern : 'solid',
        },
        marker: {
          size: 4,
          color: metric.color,
        },
        fill: chartSettings.fillArea ? 'tozeroy' : 'none',
        fillcolor: chartSettings.fillArea ? metric.color.replace(')', ', 0.1)').replace('rgb', 'rgba') : undefined,
        hovertemplate: `%{y:.3f}<extra>${metric.label}</extra>`,
        connectgaps: true,
      };

      // Handle normalized mode (scale to 0-100%)
      if (chartSettings.displayMode === 'normalized') {
        const numericValues = values.filter((v): v is number => v !== null);
        if (numericValues.length > 0) {
          const min = Math.min(...numericValues);
          const max = Math.max(...numericValues);
          const range = max - min || 1;
          trace.y = values.map(v => (v !== null ? ((v - min) / range) * 100 : null));
          trace.hovertemplate = `%{y:.1f}% (${metric.label})<extra>Original: %{customdata:.3f}</extra>`;
          trace.customdata = values;
        }
      }

      // Handle split Y-axis mode
      if (chartSettings.displayMode === 'split' && index > 0) {
        trace.yaxis = `y${index + 1}`;
      }

      return trace;
    });

    return plotTraces;
  }, [data, selectedMetrics, chartSettings]);

  // Check if we should reset the range (when preset button is clicked)
  const shouldResetRange = resetRangeTrigger !== lastResetTriggerRef.current;

  // Reset range when resetRangeTrigger changes
  useEffect(() => {
    if (resetRangeTrigger !== lastResetTriggerRef.current) {
      lastResetTriggerRef.current = resetRangeTrigger;
      // Clear saved range when resetting
      setCurrentRange({ from: null, to: null });
    }
  }, [resetRangeTrigger]);

  // Use dataExtent from hook (stable, doesn't change on zoom)
  const rangesliderExtent: [Date, Date] | null = dataExtent ? [dataExtent.from, dataExtent.to] : null;

  // Calculate Y-axis range based on visible X range
  const visibleYRange = useMemo((): [number, number] | null => {
    const visibleMetrics = selectedMetrics.filter(m => m.visible);
    if (data.length === 0 || visibleMetrics.length === 0) return null;

    // Determine the visible X range
    const xFrom = currentRange.from ? new Date(currentRange.from).getTime() : null;
    const xTo = currentRange.to ? new Date(currentRange.to).getTime() : null;

    // Filter data to visible range
    const visibleData = (xFrom && xTo)
      ? data.filter(row => {
          const t = new Date(row.createdAt).getTime();
          return t >= xFrom && t <= xTo;
        })
      : data;

    if (visibleData.length === 0) return null;

    // Find min/max across all visible metrics
    let min = Infinity;
    let max = -Infinity;

    for (const row of visibleData) {
      for (const metric of visibleMetrics) {
        const val = row.metrics?.[metric.key];
        if (typeof val === 'number' && Number.isFinite(val)) {
          if (val < min) min = val;
          if (val > max) max = val;
        }
      }
    }

    if (!Number.isFinite(min) || !Number.isFinite(max)) return null;

    // Add 5% padding
    const padding = (max - min) * 0.05 || 1;
    return [min - padding, max + padding];
  }, [data, selectedMetrics, currentRange.from, currentRange.to]);

  // Build layout with dynamic Y-axes for split mode
  const layout = useMemo(() => {
    const visibleMetrics = selectedMetrics.filter(m => m.visible);

    // Build rangeslider config - use dataExtent which doesn't change on zoom
    const rangesliderConfig = chartSettings.showRangeslider && rangesliderExtent
      ? {
          visible: true,
          range: rangesliderExtent as [any, any], // TypeScript tuple for Plotly
        }
      : chartSettings.showRangeslider
      ? { visible: true }
      : undefined;

    // Determine Y-axis range: manual > calculated from visible data > autorange
    let yAxisRange: [number, number] | undefined;
    if (chartSettings.yAxisScale === 'manual' && chartSettings.yAxisMin !== undefined && chartSettings.yAxisMax !== undefined) {
      yAxisRange = [chartSettings.yAxisMin, chartSettings.yAxisMax];
    } else if (visibleYRange && !shouldResetRange) {
      yAxisRange = visibleYRange;
    }

    const baseLayout: Partial<Plotly.Layout> = {
      ...PLOTLY_LAYOUT_DEFAULTS,
      height,
      showlegend: visibleMetrics.length <= 20,
      // Use uirevision to preserve UI state (zoom, pan, rangeslider) across data updates
      // Only change it when we actually want to reset (filter button clicked)
      uirevision: shouldResetRange ? Date.now() : 'stable',
      // In selection mode, dragging selects a horizontal x-range instead of zooming
      dragmode: selectionMode ? 'select' : 'zoom',
      selectdirection: 'h',
      xaxis: {
        ...PLOTLY_LAYOUT_DEFAULTS.xaxis,
        autorange: shouldResetRange ? true : undefined,
        range: (!shouldResetRange && currentRange.from && currentRange.to)
          ? [currentRange.from, currentRange.to]
          : undefined,
        rangeslider: rangesliderConfig,
      },
      yaxis: {
        ...PLOTLY_LAYOUT_DEFAULTS.yaxis,
        type: chartSettings.yAxisScale === 'log' ? 'log' : 'linear',
        title: {
          text: chartSettings.displayMode === 'normalized' ? 'Normalized (%)' : 'Value',
          font: { color: '#8b949e', size: 12 },
        },
        autorange: yAxisRange ? false : true,
        range: yAxisRange,
      },
      grid: chartSettings.showGrid ? undefined : { xgap: 0, ygap: 0 },
      shapes: [],
      annotations: [],
    };

    // Add split Y-axes if needed
    if (chartSettings.displayMode === 'split' && visibleMetrics.length > 1) {
      const axisWidth = Math.min(0.15, 0.8 / visibleMetrics.length);

      for (let i = 1; i < visibleMetrics.length; i++) {
        const yAxisKey = `yaxis${i + 1}`;
        (baseLayout as any)[yAxisKey] = {
          ...PLOTLY_LAYOUT_DEFAULTS.yaxis,
          overlaying: 'y',
          side: i % 2 === 0 ? 'left' : 'right',
          position: i % 2 === 0 ? axisWidth * Math.floor(i / 2) : 1 - axisWidth * Math.floor(i / 2),
          showgrid: false,
          title: { text: visibleMetrics[i].label, font: { color: visibleMetrics[i].color, size: 10 } },
          tickfont: { color: visibleMetrics[i].color, size: 10 },
        };
      }
    }

    // Add threshold lines
    if (thresholds.length > 0 && baseLayout.shapes) {
      for (const threshold of thresholds) {
        baseLayout.shapes.push({
          type: 'line',
          x0: 0,
          x1: 1,
          xref: 'paper',
          y0: threshold.value,
          y1: threshold.value,
          yref: 'y',
          line: {
            color: threshold.color,
            width: 2,
            dash: threshold.style === 'dashed' ? 'dash' : threshold.style === 'dotted' ? 'dot' : 'solid',
          },
        } as any);

        if (baseLayout.annotations) {
          baseLayout.annotations.push({
            x: 1,
            xref: 'paper',
            y: threshold.value,
            yref: 'y',
            text: threshold.label,
            showarrow: false,
            xanchor: 'left',
            font: { color: threshold.color, size: 10 },
            bgcolor: 'rgba(33, 40, 44, 0.8)',
            borderpad: 3,
          } as any);
        }
      }
    }

    // Add vertical annotation markers
    if (annotations.length > 0 && baseLayout.shapes) {
      for (const annotation of annotations.filter(a => a.type === 'vertical')) {
        baseLayout.shapes.push({
          type: 'line',
          x0: annotation.timestamp,
          x1: annotation.timestamp,
          y0: 0,
          y1: 1,
          yref: 'paper',
          line: {
            color: annotation.color,
            width: 2,
            dash: 'dot',
          },
        } as any);

        if (baseLayout.annotations) {
          baseLayout.annotations.push({
            x: annotation.timestamp,
            y: 1,
            yref: 'paper',
            text: annotation.label,
            showarrow: true,
            arrowhead: 0,
            ax: 0,
            ay: -30,
            font: { color: annotation.color, size: 10 },
            bgcolor: 'rgba(33, 40, 44, 0.9)',
            bordercolor: annotation.color,
            borderpad: 4,
          } as any);
        }
      }
    }

    // Add batch overlay bands (periods assigned to a production batch)
    if (batchOverlays.length > 0 && baseLayout.shapes) {
      for (const overlay of batchOverlays) {
        baseLayout.shapes.push({
          type: 'rect',
          xref: 'x',
          yref: 'paper',
          x0: overlay.from,
          x1: overlay.to,
          y0: 0,
          y1: 1,
          fillcolor: overlay.color,
          opacity: 0.18,
          line: { width: 1, color: overlay.color },
          layer: 'below',
        } as any);

        if (baseLayout.annotations) {
          baseLayout.annotations.push({
            x: overlay.from,
            xref: 'x',
            y: 1,
            yref: 'paper',
            text: overlay.label,
            showarrow: false,
            xanchor: 'left',
            yanchor: 'bottom',
            font: { color: overlay.color, size: 10 },
            bgcolor: 'rgba(33, 40, 44, 0.85)',
            bordercolor: overlay.color,
            borderpad: 2,
          } as any);
        }
      }
    }

    return baseLayout;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMetrics, chartSettings, height, thresholds, annotations, batchOverlays, selectionMode, shouldResetRange, rangesliderExtent, visibleYRange, currentRange]);

  // Handle zoom/pan events
  const handleRelayout = useCallback((event: any) => {
    const xStart = event['xaxis.range[0]'] || event['xaxis.range']?.[0];
    const xEnd = event['xaxis.range[1]'] || event['xaxis.range']?.[1];

    if (xStart && xEnd) {
      // Save the current range so Y-axis recalculates for visible data
      // (kept as the raw Plotly string so it round-trips back into the layout)
      setCurrentRange({ from: xStart, to: xEnd });
      if (onZoomChange) {
        onZoomChange(parsePlotlyAxisDate(xStart), parsePlotlyAxisDate(xEnd));
      }
    } else if (event['xaxis.autorange']) {
      // Clear saved range on autorange
      setCurrentRange({ from: null, to: null });
      if (onZoomChange) {
        onZoomChange(null, null);
      }
    }
  }, [onZoomChange]);

  // Handle range selection (selectionMode) - x-range drag to assign to a batch
  const handleSelected = useCallback((event: any) => {
    if (!onRangeSelect || !event) return;
    const xRange = event.range?.x;
    if (xRange && xRange.length === 2) {
      const from = parsePlotlyAxisDate(xRange[0]);
      const to = parsePlotlyAxisDate(xRange[1]);
      if (from.getTime() < to.getTime()) {
        onRangeSelect(from, to);
      }
    }
  }, [onRangeSelect]);

  // Handle click to add annotation
  const handleClick = useCallback((event: any) => {
    if (onAddAnnotation && event.points && event.points.length > 0) {
      const point = event.points[0];
      onAddAnnotation(point.x, point.y);
    }
  }, [onAddAnnotation]);

  if (traces.length === 0) {
    return (
      <div
        style={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(33, 40, 44, 0.6)',
          borderRadius: '8px',
          border: '1px solid rgba(108, 117, 125, 0.2)',
          color: '#6c757d',
          fontSize: '0.9rem',
        }}
      >
        {data.length === 0
          ? 'No data to display. Try selecting a different time range.'
          : 'Select at least one metric to display.'}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      style={{
        borderRadius: '8px',
        overflow: 'hidden',
        border: '1px solid rgba(108, 117, 125, 0.2)',
      }}
    >
      <Plot
        ref={plotRef}
        data={traces}
        layout={layout as any}
        config={PLOTLY_CONFIG}
        style={{ width: '100%', height }}
        onRelayout={handleRelayout}
        onClick={handleClick}
        onSelected={handleSelected}
        useResizeHandler
      />
    </div>
  );
});

export default PlotlyChart;
